import { create } from 'zustand';

export interface SpotRequest {
  id: string;
  building_id: string;
  user_id: string;
  requested_from: string;
  requested_until: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  created_at: string;
  updated_at: string;
  offer_count?: number;
}

export interface IncomingRequest extends SpotRequest {
  requester_apartment: string;
  requester_name: string;
}

export interface SpotOffer {
  id: string;
  request_id: string;
  spot_id: string;
  offerer_id: string;
  offerer_apartment: string;
  offerer_name: string;
  offered_from: string;
  offered_until: string;
  message?: string;
  status: 'pending' | 'accepted' | 'rejected' | 'dismissed';
  created_at: string;
  updated_at?: string;
}

export interface SpotRequestDetail extends SpotRequest {
  requester_apartment: string;
  requester_name: string;
  offers: Array<SpotOffer>;
}

export interface SpotRequestSchedule {
  id: string;
  user_id: string;
  building_id: string;
  days_of_week: number[];
  start_time: string;
  end_time: string;
  is_active: boolean;
}

interface SpotRequestState {
  // My requests
  myRequest: SpotRequest | null;
  myRequestDetail: SpotRequestDetail | null;
  myRequests: SpotRequest[];

  // Incoming requests (for offering)
  incomingRequests: IncomingRequest[];

  // Offers on my request
  incomingOffers: SpotOffer[];

  // Actions
  setMyRequest: (req: SpotRequest | null) => void;
  setMyRequestDetail: (detail: SpotRequestDetail | null) => void;
  setMyRequests: (reqs: SpotRequest[]) => void;
  setIncomingRequests: (reqs: IncomingRequest[]) => void;
  setIncomingOffers: (offers: SpotOffer[]) => void;
  addIncomingOffer: (offer: SpotOffer) => void;
  updateOfferStatus: (offerId: string, status: 'pending' | 'accepted' | 'rejected' | 'dismissed') => void;
  clearMyRequest: () => void;
  clearMyRequestDetail: () => void;
}

export const useSpotRequestStore = create<SpotRequestState>((set) => ({
  myRequest: null,
  myRequestDetail: null,
  myRequests: [],
  incomingRequests: [],
  incomingOffers: [],

  setMyRequest: (req) => set({ myRequest: req }),
  setMyRequestDetail: (detail) => set({ myRequestDetail: detail }),
  setMyRequests: (reqs) => set({ myRequests: reqs }),
  setIncomingRequests: (reqs) => set({ incomingRequests: reqs }),
  setIncomingOffers: (offers) => set({ incomingOffers: offers }),

  addIncomingOffer: (offer) =>
    set((state) => ({
      incomingOffers: [offer, ...state.incomingOffers],
      myRequestDetail: state.myRequestDetail
        ? {
            ...state.myRequestDetail,
            offers: [offer, ...state.myRequestDetail.offers],
          }
        : null,
    })),

  updateOfferStatus: (offerId, status) =>
    set((state) => ({
      incomingOffers: state.incomingOffers.map((o) =>
        o.id === offerId ? { ...o, status } : o,
      ),
      myRequestDetail: state.myRequestDetail
        ? {
            ...state.myRequestDetail,
            offers: state.myRequestDetail.offers.map((o) =>
              o.id === offerId ? { ...o, status } : o,
            ),
          }
        : null,
    })),

  clearMyRequest: () => set({ myRequest: null }),
  clearMyRequestDetail: () => set({ myRequestDetail: null }),
}));
